<template>
  <section class="bg-light py-4">
    <!-- Navigation -->
    <div class="row pb-3">
      <div class="col">
        <router-link to="/rewards" class="text-primary-link">
          {{ $t('message.rewards.title') }}
        </router-link>
        <i class="fal fa-angle-right"></i>
        <router-link to="/user-rewards" class="text-primary-link">
          {{ $t('message.rewards.my_redeemed_rewards') }}
        </router-link>
        <i class="fal fa-angle-right"></i> {{ $t('message.rewards.detail') }}
      </div>
    </div>

    <!-- User Reward Detail -->
    <div class="row" v-if="userReward">
      <div class="col-lg-6 offset-lg-3">
        <div class="card">
          <img class="card-img-top" 
               :src="userReward.reward.image_url" 
               :alt="userReward.reward.name_th">
          <div v-if="userReward.is_used">
            <div class="px-3 pt-3 text-info d-flex justify-content-center align-items-center">
              <div><i class="fas fa-check-circle fa-lg"></i></div> 
              <div class="ml-2">{{ $t('message.rewards.used_on') }}: {{ userReward.used_at | prettyDate }}</div>
            </div>
            <hr>
          </div>
          
          <div class="card-body">
            <div>
              <span class="badge text-badge-small badge-pill py-1 px-2"
                    :class="getUserRewardStatus(userReward).cssClass">
                {{ getUserRewardStatus(userReward).text }}
              </span>
            </div>
            <div class="pt-3">
              <h3 class="mb-1">{{ ($i18n.locale === 'th') ? userReward.reward.title_th : userReward.reward.title_en }}</h3>
            </div>
            <div class="pt-2" v-if="userReward.promo_code">
              {{ $t('message.promo_code.code') }}: {{ userReward.promo_code.code }}
            </div>
            <div class="pt-2 text-secondary" v-if="userReward.expire_date">
              {{ $t('message.promo_code.expiring') }}: {{ userReward.expire_date | prettyOnlyDate }}
            </div>
            <div class="pt-3 text-secondary" v-if="userReward.redeemed_at">
              {{ $t('message.rewards.redeemed_on') }}: {{ userReward.redeemed_at | prettyDate }}
            </div>
            
            <div class="pt-4" v-if="userReward.wogi_order_status || userReward.reward.type === 'voucher'">
              <span v-if="userReward.wogi_order_status === 'pending'">
                <i class="fal fa-stopwatch text-info"></i> {{ $t('message.rewards.processing_voucher_remark') }}
              </span>
              <span v-else-if="userReward.wogi_order_status === 'failed'">
                <i class="fal fa-exclamation-triangle text-danger"></i> {{ $t('message.rewards.redeem_voucher_error') }}
              </span>
              <div class="pt-3" v-if="userReward.wogi_order_status === 'pending'">
                <button class="btn btn-info btn-block" @click="fetchUserRewardDetail(id)">{{ $t('message.button.refresh') }}</button>
              </div>
              <div class="pt-3" v-if="userReward.wogi_order_status === 'successful' || userReward.reward.type === 'voucher'">
                <button class="btn btn-info btn-block" @click="checkUsedReward()" :disabled="isLoading">
                  {{ isLoading ? $t('message.button.loading') : $t('message.rewards.use_reward') }}
                </button>
              </div>
            </div>

            <div class="pt-4" v-if="userReward.promo_code && !userReward.promo_code_usage && !userReward.is_expired">
              <div class="text-remark pb-3">
                {{ $t('message.rewards.promo_code_remark') }}
              </div>
              <router-link to="/booking/start" class="btn btn-info btn-block">
                {{ $t('message.booking.new_booking.book_a_service') }}
              </router-link>
            </div>
            <div v-if="userReward.delivery_info">
              <hr>
              <div class="mb-2"><b>{{ $t('message.rewards.delivery_info') }}</b></div>
              <div v-if="userReward.reward.delivery_info_type === 'email_tel'">
                <ul class="list-unstyled mb-0">
                  <li>{{ $t('message.rewards.contact_name') }}: {{ userReward.delivery_info.tel }}</li>
                  <li>{{ $t('message.rewards.contact_email') }}: {{ userReward.delivery_info.email }}</li>
                </ul>
              </div>
              <div v-else>
                <ul class="list-unstyled mb-0">
                  <li>{{ $t('message.rewards.contact_name') }}: {{ userReward.delivery_info.name }}</li>
                  <li>{{ $t('message.rewards.contact_tel') }}: {{ userReward.delivery_info.tel }}</li>
                  <li>
                    <span v-if="userReward.delivery_info.village">
                      {{ userReward.delivery_info.village }}
                    </span>
                    <span v-if="userReward.delivery_info.room_no">
                      {{ $t('message.shipping_address.room_no') }} {{ userReward.delivery_info.room_no }}
                    </span>
                    <span v-if="userReward.delivery_info.house_no">
                      {{ $t('message.shipping_address.house_no') }} {{ userReward.delivery_info.house_no }}
                    </span>
                    <span v-if="userReward.delivery_info.village_no">
                      {{ $t('message.shipping_address.village_no') }} {{ userReward.delivery_info.village_no }}
                    </span>
                    <span v-if="userReward.delivery_info.soi">
                      {{ $t('message.shipping_address.soi') }} {{ userReward.delivery_info.soi }}
                    </span>
                    <span>
                      {{ $t('message.shipping_address.road') }} {{ userReward.delivery_info.road }}
                    </span>
                    <span v-if="userReward.delivery_info.sub_district">
                      <span v-if="$i18n.locale === 'th'">{{ $t('message.shipping_address.sub_district') }}</span>
                      {{ userReward.delivery_info.sub_district | multiLangName }}
                    </span>
                    <span v-if="userReward.delivery_info.district">
                      <span v-if="$i18n.locale === 'th'">{{ userReward.delivery_info.address_province_id != 1 ? 'อ.' : '' }}</span>{{ userReward.delivery_info.district | multiLangName }}
                    </span>
                    <span v-if="userReward.delivery_info.address_province">
                      <span v-if="$i18n.locale === 'th'">จ.</span>{{ userReward.delivery_info.address_province | multiLangName }}
                    </span>
                    <span>{{ userReward.delivery_info.zip_code }}</span>
                  </li>
                </ul>
              </div>
            </div>
            <div class="mt-3" v-else-if="userReward.reward.delivery_info_type && !userReward.delivery_info">
              <router-link :to="`/user-rewards/${userReward.id}/delivery-info`" class="btn btn-outline-info btn-block">
                {{ $t('message.rewards.add_delivery_info') }}
              </router-link>
            </div>
            <div class="mt-3 text-secondary" v-if="userReward.delivery_info && userReward.sent_at">
              {{ $t('message.rewards.sent_on') }}: {{ userReward.sent_at | prettyDate }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <loader></loader>
    </div>
  </section>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex'
  import moment from 'moment-timezone'
  import Loader from '../loader/Loader'
  import userRewardHelpers from '../../helpers/userRewardHelpers'

  export default {
    components: {
      Loader
    },
    data () {
      return {
        id: this.$route.params.id,
        isLoading: false
      }
    },
    computed: {
      ...mapGetters({
        user: 'auth/getUser'
      }),
      ...mapState('userReward', ['userReward'])
    },
    methods: {
      ...mapActions('userReward', ['fetchUserRewardDetail', 'useReward']),
      getUserRewardStatus (userReward) {
        return userRewardHelpers.getUserRewardStatus(userReward)
      },
      isExpired (userReward) {
        const currentDate = moment().format('YYYY-MM-DD')
        return currentDate > userReward.expire_date
      },
      checkUsedReward () {
        if (this.userReward.is_used) {
          window.open(this.userReward.page_url, '_blank')
        } else {
          this.displayUseRewardConfirmDialog()
        }
      },
      displayUseRewardConfirmDialog () {
        this.$swal({
          html: `
            <div class="text-center">
              <img src="https://beneat.s3.ap-southeast-1.amazonaws.com/images/icon_use_reward.svg" width="64"/>
              <h4 class="mt-3">${this.$i18n.t('message.rewards.use_reward_dialog_content01')}</h4>
              <div class="mt-3 text-secondary">${this.$i18n.t('message.rewards.use_reward_dialog_content02')}</div>
              <div class="mt-3 text-info">${this.$i18n.t('message.rewards.use_reward_dialog_content03')}</div>
            </div>`,
          reverseButtons: true,
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          confirmButtonText: this.$i18n.t('message.button.confirm'),
          cancelButtonText: this.$i18n.t('message.button.cancel')
        }).then(async () => {
          this.isLoading = true
          await this.useReward(this.userReward.id)
          this.isLoading = false
          window.open(this.userReward.page_url, '_blank')
        })
      }
    },
    mounted () {
      this.fetchUserRewardDetail(this.id)
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/reward/UserRewardDetail.vue